/**
 * Centered card layout used by every public auth page (signup, login,
 * check-email, join, no-workspace).
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 4.1`.
 *
 * Pages just render their form/content; this owns the page chrome
 * (background, vertical centering, card frame, title + optional subtitle).
 */

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@cometchat-ai/ui-dashboard";
import type { ReactNode } from "react";

export interface AuthLayoutProps {
  title: string;
  subtitle?: ReactNode;
  /** Optional mark rendered centered above the title (e.g. a status icon). */
  icon?: ReactNode;
  children: ReactNode;
}

export function AuthLayout({ title, subtitle, icon, children }: AuthLayoutProps) {
  return (
    <main className="grid min-h-dvh place-items-center bg-muted px-4 py-12">
      <Card className="w-full max-w-md rounded-3xl bg-card shadow-elevated">
        <CardHeader className={icon ? "items-center text-center" : "text-center"}>
          {icon}
          {/*
           * shadcn's <CardTitle> renders a <div>; tag it with the heading
           * role + level so screen readers and `getByRole('heading')`
           * recognise it.
           */}
          <CardTitle
            role="heading"
            aria-level={1}
            className="exp-heading-font text-xl tracking-tight"
          >
            {title}
          </CardTitle>
          {subtitle ? (
            <CardDescription className="font-normal text-muted-foreground">
              {subtitle}
            </CardDescription>
          ) : null}
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </main>
  );
}
